// Copyright (c) Docugami, Inc. All rights reserved.

import { IDocumentCommand } from "components/file_management/DocumentCommand";
import { TextEntryDialog } from "components/shell/utils/TextEntryDialog";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { renameCluster } from "store/clusters/actions";
import { getClusterInfoById } from "store/state_helpers";
import { DocSetRenameCancelledSchema, DocSetRenameClickedSchema, DocSetRenameConfirmedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { RequestState, useAsyncHelper } from "./AsyncHelper";
import { useAppDispatch } from 'hooks/redux';

export function useRenameDocSetCommand(docSetId: string, onComplete: (error?: any) => void): IDocumentCommand {
    const [isActive, setIsActive] = useState(false);

    const docSetInfo = useSelector((state: AppState) => getClusterInfoById(state, docSetId));
    const { execute, state, error } = useAsyncHelper("Rename DocSet", docSetId);
    const dispatch = useAppDispatch();

    function getVisual(): JSX.Element | null {
        if (!isActive) {
            return null;
        }

        return (
            <TextEntryDialog
                hidden={!isActive}
                originalText={docSetInfo?.label! ?? ''}
                placeholderText="Enter a name"
                title="Rename Doc Set"
                confirmButtonText="Rename"
                dismissButtonText="Cancel"
                confirmedButtonDisabled={value => !validateInput(value)}
                confirmedCallback={value => execute(confirm(value))}
                dismissedCallback={() => dismiss()} />
        );
    }

    async function confirm(value: string): Promise<void> {
        Telemetry.trackEvent<DocSetRenameConfirmedSchema>({
            docSetId: docSetId,
            eventName: TelemetryEvent.DocSetRenameConfirmed
        });

        value = value.trim();
        if (!!value && value !== docSetInfo.label) {
            await dispatch(renameCluster(docSetId, value));
        }
    }

    function onExecute() {
        Telemetry.trackEvent<DocSetRenameClickedSchema>({
            docSetId: docSetId,
            eventName: TelemetryEvent.DocSetRenameClicked
        });
        setIsActive(true);
    }

    function dismiss() {
        Telemetry.trackEvent<DocSetRenameCancelledSchema>({
            docSetId: docSetId,
            eventName: TelemetryEvent.DocSetRenameCancelled
        });
        complete();
    }

    function validateInput(value: string): boolean {
        const trimmed = value.trim();
        return trimmed.length > 0 && trimmed !== docSetInfo.label?.trim();
    }

    function complete(error?: any) {
        setIsActive(false);
        onComplete(error);
    }

    useEffect(() => {
        if (state === RequestState.Complete || state === RequestState.Error) {
            complete(error);
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state])

    return {
        id: "Rename DocSet",
        label: "Rename",
        isActive: isActive,
        isEnabled: !!docSetInfo,
        visual: getVisual(),
        execute: onExecute
    }
}
